// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import apiService from '../../../../share/util/api/service';
import { createObdDtcTable } from '../../../../util/obd/diag';

export default {
  name: "obdPermanentDtcPage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false }
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      systemName: '',
      measuresId: '',
      obdDataId: '',
      selectedSystem: null,
      isObd: true,
      measuredDataItems: [],
    };
  },

  watch: {
    language() {
      this.updateColumns(this.systemName);
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * パーマネント計測データ一覧取得
     * @param {String} obdDataId OBD Data ID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     * @param {String} systemNo ECU No
     */
    async createObdPermanentDiagCodeMeasuresList(obdDataId, measuresId, system, systemNo) {
      try {
        this.systemName = system;
        this.obdDataId = obdDataId;
        this.measuresId = measuresId;
        this.processing = true;

        const params = { region_info: this.cachedProjectDataSource };
        // Get APIを呼び出す
        const response = await apiService.getParamCall(
          apiService.OBD,
          `/obd/${obdDataId}/measures/${measuresId}/dtc`,
          params,
          this.operationScreen
        );

        // APIレスポンスの検証
        handler.validate(handler.validateTypes.all, response, this, null, () => {
          this.recollectedData = response.data;
          this.updateColumns();
          this.updateDTCTable(response, systemNo);
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定
     */
    async updateColumns() {
      this.measuredDataColumns = [
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: 'message.header_vehicle_dtc_table_igon_counts' },
        { name: 'igonTime', label: 'message.header_vehicle_dtc_table_igon_times' },
        { name: 'counterType', label: 'fixed_resource.header_vehicle_dtc_table_type', useResource: true },
        { name: 'ffd', label: 'fixed_resource.header_vehicle_dtc_table_ffd', useButton: true, useResource: true },
      ];
    },

    /**
     * テーブル情報更新処理を行う
     * @param {object} response レスポンスデータ
     * @param {string} systemNo ECU No
     */
    updateDTCTable(response, systemNo) {
      // システムに紐づく DTC があるかチェックする
      let filteredDtcSystems = [];
      if (response) {
        filteredDtcSystems = response.data.data.filter(dtc => dtc.system === systemNo);
      }

      // 第3引数は全DTC でしか使用していないため、暫定で入れている
      this.measuredDataItems = createObdDtcTable(filteredDtcSystems, systemNo, this.systemName, this);
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData, false, true);
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.dtcTestDataExport();
    },

    /** DTCテストデータエクスポート  */
    dtcTestDataExport() {
      this.$emit('dtcTestDataExport', this.recollectedData);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};